import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/assets';
import Dialog from '../../components/Dialog';
import Loading from './Loading';
import { getItems, exchangeItem, Item } from '../../api/items';
import './QiankundaiModal.css';

interface TreasureItem extends Partial<Item> {
  id: string;
  icon: string;
  isNextButton?: boolean;
  value?: string;
  grade?: string;
  name?: string;
  owned?: boolean;
}

interface QiankundaiModalProps {
  visible: boolean;
  onClose: () => void;
}

const QiankundaiModal: React.FC<QiankundaiModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation(['game']);
  const [items, setItems] = useState<TreasureItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [userBalance, setUserBalance] = useState<number>(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isExchanging, setIsExchanging] = useState(false);

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;
  const getImgSrc = (name: string) => getImageUrl('gameplay/' + name);

  useEffect(() => {
    if (visible) {
      fetchItems();
    }
  }, [visible]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getItems('qiankundai');
      
      let formattedItems: TreasureItem[] = data.items.map(item => ({
        ...item,
        value: item.price,
        owned: item.isOwned,
      }));

      // Ensure minimum 9 items and fill grid (multiple of 3)
      const minItems = 9;
      const itemsPerRow = 3;
      const totalNeeded = Math.max(minItems, Math.ceil(formattedItems.length / itemsPerRow) * itemsPerRow);
      
      if (formattedItems.length < totalNeeded) {
        const paddingCount = totalNeeded - formattedItems.length;
        for (let i = 0; i < paddingCount; i++) {
          formattedItems.push({
            id: `padding-${i}`,
            icon: 'qiankuandai_item_icon.png', // Empty slot style
            isNextButton: false, // Just empty slot
          });
        }
      }

      setItems(formattedItems);
      // Parse balance safely
      const balance = parseInt(data.userBalance.replace(/,/g, ''), 10) || 0;
      setUserBalance(balance);
      
      // Default select first real item if available
      if (data.items.length > 0) {
        setSelectedId(data.items[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch items', error);
      toast.error(t('common.loadingError', 'Loading failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (id: string, isNext?: boolean) => {
    if (isNext) {
      // Handle next page logic if needed
      console.log('Next page clicked');
      return;
    }
    // Prevent selecting padding items
    if (id.startsWith('padding-')) return;

    // Update selection
    setSelectedId(id);
  };

  // Logic to check if exchange is enabled
  const selectedItem = items.find(item => item.id === selectedId);
  const itemValue = selectedItem?.value ? parseInt(selectedItem.value.replace(/,/g, ''), 10) : 0;
  const isExchangeEnabled = selectedItem && !selectedItem.isNextButton && !selectedItem.id.startsWith('padding-') && !selectedItem.owned && userBalance >= itemValue;

  // Calculate owned count
  const ownedCount = items.filter(item => item.owned && !item.id.startsWith('padding-')).length;

  const handleExchangeClick = () => {
    if (isExchangeEnabled) {
      setShowConfirmDialog(true);
    } else {
      if (!selectedItem || selectedItem.id.startsWith('padding-')) {
        toast(t('wanxiangyi.selectItem'));
      } else if (selectedItem.owned) {
        toast(t('wanxiangyi.alreadyOwned'));
      } else if (userBalance < itemValue) {
        toast(t('common.insufficientBalance'));
      }
    }
  };

  const handleConfirmExchange = async () => {
    if (!selectedItem) return;

    setShowConfirmDialog(false);
    setLoading(true);
    
    try {
      const response = await exchangeItem(selectedItem.id, 'qiankundai');
      setLoading(false);

      if (response.success) {
        toast.success(t('common.exchangeSuccess'));
        // Refresh items to get latest data
        await fetchItems();
      }
    } catch (error: any) {
      setLoading(false);
      console.error('Exchange failed', error);
      toast.error(error.message || t('common.exchangeFailed', 'Exchange failed'));
    }
  };

  if (!visible && items.length === 0 && !loading) return null;

  return (
    <div className={`qiankundai-modal-container ${visible ? 'visible' : ''}`}>
      <div className="qiankundai-backdrop" style={{ backgroundImage: getImg('popup-backdrop.png') }} onClick={onClose} />

      <div className="qiankundai-content-wrapper">
        <div className="qiankundai-closet-container">
          {/* Background Layers - Reusing Wanxiangyi backgrounds as per prototype */}
          <div className="qiankundai-background-layer" style={{ backgroundImage: getImg('wanxiangyi-background-3.png') }} />

          {/* Header Area */}
          <div className="qiankundai-header-area" style={{ backgroundImage: getImg('qiankuandai_title_icon.png') }} />
          
          {/* Close Button */}
          <div 
            className="qiankundai-close-button" 
            style={{ backgroundImage: getImg('wanxiangyi-button-close.png') }} 
            onClick={onClose}
          />

          {/* Title - Note: The HTML prototype has a typo "乾款袋" but design likely means "乾坤袋" */}
          <span className="qiankundai-page-title">{t('qiankundai.title')}</span>

          {loading && <Loading text={t('common.loading')} />}

          {!loading && (
            <>
              {/* Info Panel - Added */}
              <div className="qk-info-panel" style={{ backgroundImage: getImg('qk_info_panel.png') }}>
                <div className="qk-button-left" style={{ backgroundImage: getImg('qk_small_button.png') }}>
                  <span className="qk-button-text">{t('common.owned')}*{ownedCount}</span>
                </div>
                
                <div className="qk-value-display" style={{ backgroundImage: getImg('qk_value_display.png') }}>
                  <div className="qk-coin-icon" style={{ backgroundImage: getImg('reward-icon.png') }} />
                  <span className="qk-value-text">{userBalance.toLocaleString()}</span>
                </div>

                <div 
                  className="qk-button-right" 
                  style={{ backgroundImage: getImg('qk_small_button.png') }}
                  onClick={handleExchangeClick}
                >
                  <span className="qk-button-text">{t('common.exchange')}</span>
                  {!isExchangeEnabled && (
                    <div className="qk-exchange-button-disabled" style={{ backgroundImage: getImg('qk_button_disabled.png') }} />
                  )}
                  <div className="qk-checkbox-dot" style={{ backgroundImage: getImg('qk_checkbox_dot.png') }} />
                </div>
              </div>

              {/* Cloth Grid */}
              <div className="qiankundai-cloth-grid">
                {items.map((item, index) => {
                  const isSelected = item.id === selectedId;
                  const isNext = !!item.isNextButton;

                  return (
                    <div 
                      key={item.id} 
                      className={`qiankundai-cloth-slot ${isSelected ? 'slot-selected' : ''}`}
                      style={{ 
                        backgroundImage: isSelected 
                          ? getImg('wanxiangyi-background-highlighted.png') 
                          : getImg('wanxiangyi-background-default.png') 
                      }}
                      onClick={() => handleItemClick(item.id, isNext)}
                    >
                      {/* Grade Tag (Top Left) */}
                      {!isNext && item.grade && (
                        <div className="slot-tag" style={{ backgroundImage: getImg('qk_item_icon.png') }}>
                          <span>{item.grade}</span>
                        </div>
                      )}

                      {/* Lock Icon (Top Right) */}
                      {!isNext && !item.owned && (
                        <div className="slot-lock" style={{ backgroundImage: getImg('qk_lock_icon.png') }} />
                      )}

                      {/* Item Image */}
                      <img 
                        src={getImgSrc(item.icon)} 
                        className="qiankundai-cloth-item" 
                        alt={`treasure-${index}`} 
                      />

                      {/* Name Tag */}
                      {!isNext && item.name && (
                        <div className="slot-name-tag" style={{ backgroundImage: getImg('qk_name_tag.png') }}>
                          <span>{item.name}</span>
                        </div>
                      )}

                      {/* Value Info */}
                      {!isNext && item.value && (
                        <div className="slot-value-info">
                          <div className="value-label">{t('common.value')}</div>
                          <div className="value-amount">
                            <div className="coin-icon" style={{ backgroundImage: getImg('reward-icon.png') }} />
                            <span>{item.value}</span>
                          </div>
                        </div>
                      )}

                      {/* Mask Overlay for unselected items */}
                      {!isSelected && (
                        <div className="qiankundai-cloth-slot-mask" style={{ backgroundImage: getImg('wanxiangyi-mask-overlay.png') }} />
                      )}

                      {/* Next Button Overlay */}
                      {isNext && (
                        <img 
                          src={getImgSrc('wanxiangyi-button-next.png')} 
                          className="qiankundai-cloth-item-next" 
                          alt="next page" 
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
      
      <Dialog
        isOpen={showConfirmDialog}
        title={t('common.tip')}
        message={
          selectedItem 
            ? t('qiankundai.exchangeMessage', { value: selectedItem.value, grade: selectedItem.grade, name: selectedItem.name })
            : ''
        }
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        onConfirm={handleConfirmExchange}
        onCancel={() => setShowConfirmDialog(false)}
      />

      {isExchanging && <Loading text={t('common.exchanging')} />}
    </div>
  );
};

export default QiankundaiModal;
